import React, { useCallback, useEffect, useState } from 'react';
import { Formik, Form, Field } from 'formik';
import { toast } from 'react-toastify';
import {
  Accordion, AccordionDetails, AccordionSummary, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControl, InputLabel
  , MenuItem, Select, TextField, Typography
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { green } from '@material-ui/core/colors';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

import { i18n } from '../../translate/i18n';
import api from '../../services/api';
import toastError from '../../errors/toastError';

import { useGlobalRegisters } from '../../context/GlobalRegisters/GlobalRegistersContext';

const useStyles = makeStyles(theme => ({
  btnWrapper: {
    position: 'relative'
  }

  , buttonProgress: {
    color: green[500]
    , position: 'absolute'
    , top: '50%'
    , left: '50%'
    , marginTop: -12
    , marginLeft: -12
  }

  , floatingButton: {
    transition: 'transform 0.30s'
    , '&:hover': { transform: 'translateY(-5px)' }
  }

  , typography: {
    fontWeight: 'bold'
    , fontSize: '1.125rem'
  }

  , inputField: {
    '& .MuiSvgIcon-root': { color: theme.palette.text.primary }
    , '& .MuiInputBase-input.Mui-disabled': { color: theme.palette.text.primary, filter: 'brightness(0.75)' }
    , '& .MuiInputLabel-root.Mui-disabled': { color: '#606163' }
  }
}));

const TemplateModal = ({ open, onClose, template }) => {
  //  ****************
  //  ** Properties **
  //  ****************
  const classes = useStyles();

  const shouldDisableFields = !!template;
  const INITIAL_STATE_TEMPLATE_TO_UPSERT = { templateUuid: '', whatsappBusinessAccountUuid: '', name: '', category: 'UTILITY', parameter_format: 'NAMED', component_header: '', component_body: '', component_footer: '' };
  const FIELD_NAME = { MIN_LENGTH: 3, MAX_LENGTH: 255, PATTERN: /[^a-z_]+/g };
  const FIELD_COMPONENTS_JSON = { MIN_LENGTH: 3, HEADER: { MAX_LENGTH: 60 }, BODY: { MAX_LENGTH: 1024 }, FOOTER: { MAX_LENGTH: 60 } };
  
  const [templateToUpsert, setTemplateToUpsert] = useState(INITIAL_STATE_TEMPLATE_TO_UPSERT);
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);

  const { isLoading, allWhatsappBusinessAccountUuids } = useGlobalRegisters();

  //  *******************
  //  ** Use Callbacks **
  //  *******************
  const extractComponentCallback = useCallback(targetComponent => {
    if (!template?.components_json) return '';
    return template.components_json.find(component => component.type === targetComponent)?.text ?? '';
  }, [template?.components_json]);

  //  *****************
  //  ** Use Effects **
  //  *****************
  useEffect(() => {
    if (!open) return;

    if (!template) {
      if (isLoading) return;

      setTemplateToUpsert(previousTemplateToUpsert => ({
        ...previousTemplateToUpsert
        , whatsappBusinessAccountUuid: allWhatsappBusinessAccountUuids.length > 0 ? allWhatsappBusinessAccountUuids[0] : ''
      }));

      return;
    }

    setTemplateToUpsert({
      templateUuid: template.templateUuid
      , whatsappBusinessAccountUuid: template.whatsappBusinessAccountUuid
      , name: template.name
      , category: template.category
      , parameter_format: template.parameter_format
      , component_header: extractComponentCallback('HEADER')
      , component_body: extractComponentCallback('BODY')
      , component_footer: extractComponentCallback('FOOTER')
    });
  }, [open, template, isLoading, allWhatsappBusinessAccountUuids, extractComponentCallback]);

  //  *************
  //  ** Methods **
  //  *************
  const validateComponentsJsonText = (component, type) => {
    const trimmedComponent = component.trim();
    if (type === 'BODY') return trimmedComponent.length >= FIELD_COMPONENTS_JSON.MIN_LENGTH && trimmedComponent.length <= FIELD_COMPONENTS_JSON.BODY.MAX_LENGTH;
    
    return trimmedComponent.length > 0
      ? trimmedComponent.length >= FIELD_COMPONENTS_JSON.MIN_LENGTH && trimmedComponent.length <= FIELD_COMPONENTS_JSON[type].MAX_LENGTH
      : true;
  };

  const validateTemplateStatusUpdate = (status) => {
    return ['APPROVED', 'REJECTED', 'PAUSED'].includes(status);
  };

  const handleClose = () => {
    onClose();
    setTimeout(() => setTemplateToUpsert(INITIAL_STATE_TEMPLATE_TO_UPSERT), 100);
  };

  const handleNameInputOnChange = (event, setFieldValue) => {
    setFieldValue(event.target.name, event.target.value.toLowerCase().replace(FIELD_NAME.PATTERN, ''));
  };

  const handleSubmit = async (values, actions) => {
    if (isFormSubmitting) return;

    try {
      setIsFormSubmitting(true);
      actions.setSubmitting(true);

      const isComponentHeaderValid = validateComponentsJsonText(values.component_header, 'HEADER');
      const isComponentBodyValid = validateComponentsJsonText(values.component_body, 'BODY');
      const isComponentFooterValid = validateComponentsJsonText(values.component_footer, 'FOOTER');
      
      if (!isComponentHeaderValid || !isComponentBodyValid || !isComponentFooterValid) {
        if (!isComponentHeaderValid) {
          toast.info(
            i18n
              .t('templateModal.toasts.invalidComponentContent')
              .replace('<param>', 'HEADER')
              .replace('<param>', FIELD_COMPONENTS_JSON.MIN_LENGTH)
              .replace('<param>', FIELD_COMPONENTS_JSON.HEADER.MAX_LENGTH)
          );

          return;
        }

        if (!isComponentBodyValid) {
          toast.info(
            i18n
              .t('templateModal.toasts.invalidComponentContent')
              .replace('<param>', 'BODY')
              .replace('<param>', FIELD_COMPONENTS_JSON.MIN_LENGTH)
              .replace('<param>', FIELD_COMPONENTS_JSON.BODY.MAX_LENGTH)
          );

          return;
        }

        
        toast.info(
          i18n
            .t('templateModal.toasts.invalidComponentContent')
            .replace('<param>', 'FOOTER')
            .replace('<param>', FIELD_COMPONENTS_JSON.MIN_LENGTH)
            .replace('<param>', FIELD_COMPONENTS_JSON.FOOTER.MAX_LENGTH)
        );

        return;
      }

      if (template) {
        if (!validateTemplateStatusUpdate(template.status)) {
          toast.warning(i18n.t('templates.toasts.invalidStatus'));
          return;
        }

        await api.put(`/officialApi/template/${template.templateUuid}`, values);
      }
      else {
        await api.post('/officialApi/template', values);
      }

      handleClose();
    }
    catch (exception) {
      const isZodException = exception.response?.data?.isZodException;

      if (isZodException) {
        toast.warning(
          i18n
            .t('templateModal.toasts.zodValidationException')
            .replace('<param>', JSON.stringify(exception.response?.data?.message?.fields ?? '-'))
        );
        return;
      }

      console.log('handleSubmit exception:', exception);
      toastError(exception);
    }
    finally {
      actions.setSubmitting(false);
      setIsFormSubmitting(false);
    }
  };

  //  ************
  //  ** Return **
  //  ************
  return (
    <Dialog open={open} onClose={handleClose} maxWidth='xs' scoll='paper' fullWidth>
      <DialogTitle>
        {template ? i18n.t('templateModal.titleUpdate') : i18n.t('templateModal.titleInsert')}
      </DialogTitle>

      <Formik
        initialValues={templateToUpsert}
        enableReinitialize={true}
        onSubmit={(values, actions) => void handleSubmit(values, actions)}
      >
        {({ values, touched, errors, isSubmitting, setFieldValue }) => (
          <Form>
            <DialogContent dividers>
              {/* ---- Name ---- */}
              <Field
                as={TextField}
                name='name'
                variant='outlined'
                margin='dense'
                autoComplete='off'
                className={classes.inputField}
                label={i18n.t('templateModal.fields.name')}
                error={touched.name && Boolean(errors.name)}
                helperText={touched.name && errors.name}
                InputProps={{ inputProps: { minLength: FIELD_NAME.MIN_LENGTH, maxLength: FIELD_NAME.MAX_LENGTH } } }
                onChange={(event) => handleNameInputOnChange(event, setFieldValue)}
                autoFocus={!shouldDisableFields}
                disabled={shouldDisableFields}
                fullWidth
                required
              />

              {/* ---- WABA UUID ---- */}
              <FormControl variant='outlined' margin='dense' fullWidth required>
                <InputLabel>{i18n.t('templateModal.fields.waba')}</InputLabel>

                <Field
                  as={Select}
                  name='whatsappBusinessAccountUuid'
                  label={i18n.t('templateModal.fields.waba')}
                  className={classes.inputField}
                  disabled={shouldDisableFields}
                  required
                >
                  {allWhatsappBusinessAccountUuids.map(uuid => (<MenuItem value={uuid}>{uuid}</MenuItem>))}
                </Field>
              </FormControl>

              {/* ---- Category ---- */}
              <FormControl variant='outlined' margin='dense' fullWidth required>
                <InputLabel>{i18n.t('templateModal.fields.category')}</InputLabel>

                <Field
                  as={Select}
                  name='category'
                  label={i18n.t('templateModal.fields.category')}
                  className={classes.inputField}
                  required
                >
                  <MenuItem value='UTILITY'>{i18n.t('templateModal.values.category.utility')}</MenuItem>
                  <MenuItem value='MARKETING'>{i18n.t('templateModal.values.category.marketing')}</MenuItem>
                </Field>
              </FormControl>

              {/* ---- Parameter Format ---- */}
              <FormControl variant='outlined' margin='dense' fullWidth required>
                <InputLabel>{i18n.t('templateModal.fields.parameterFormat')}</InputLabel>

                <Field
                  as={Select}
                  name='parameter_format'
                  label={i18n.t('templateModal.fields.parameterFormat')}
                  className={classes.inputField}
                  required
                >
                  <MenuItem value='NAMED'>{i18n.t('templateModal.values.parameterFormat.named')}</MenuItem>
                  <MenuItem value='POSITIONAL'>{i18n.t('templateModal.values.parameterFormat.positional')}</MenuItem>
                </Field>
              </FormControl>

              {/* ---- Components JSON ---- */}
              <br /><br />
              <Divider />
              <Divider />
              <br />
              
              <Typography gutterBottom className={classes.typography}>{i18n.t('templateModal.fields.componentsJson')}</Typography>

              {/* ---- Components JSON: Header ---- */}
              <Accordion>
                <AccordionSummary className={classes.inputField} expandIcon={<ExpandMoreIcon />}>
                  {i18n.t('templateModal.values.componentsJson.accordionSummary.header')}
                </AccordionSummary>

                <AccordionDetails>
                  <Field
                    as={TextField}
                    name='component_header'
                    variant='outlined'
                    margin='dense'
                    autoComplete='off'
                    label={`${values.component_header.length}/${FIELD_COMPONENTS_JSON.HEADER.MAX_LENGTH}`}
                    InputProps={{ inputProps: { maxLength: FIELD_COMPONENTS_JSON.HEADER.MAX_LENGTH } } }
                    fullWidth
                  />
                </AccordionDetails>
              </Accordion>

              {/* ---- Components JSON: Body ---- */}
              <Accordion expanded>
                <AccordionSummary className={classes.inputField} expandIcon={<ExpandMoreIcon />}>
                  {i18n.t('templateModal.values.componentsJson.accordionSummary.body')}*
                </AccordionSummary>

                <AccordionDetails>
                  <Field
                    as={TextField}
                    name='component_body'
                    variant='outlined'
                    margin='dense'
                    autoComplete='off'
                    label={`${values.component_body.length}/${FIELD_COMPONENTS_JSON.BODY.MAX_LENGTH}`}
                    InputProps={{ inputProps: { minLength: FIELD_COMPONENTS_JSON.MIN_LENGTH, maxLength: FIELD_COMPONENTS_JSON.BODY.MAX_LENGTH } } }
                    autoFocus={shouldDisableFields}
                    fullWidth
                    required
                  />
                </AccordionDetails>
              </Accordion>

              {/* ---- Components JSON: Footer ---- */}
              <Accordion>
                <AccordionSummary className={classes.inputField} expandIcon={<ExpandMoreIcon />}>
                  {i18n.t('templateModal.values.componentsJson.accordionSummary.footer')}
                </AccordionSummary>

                <AccordionDetails>
                  <Field
                    as={TextField}
                    name='component_footer'
                    variant='outlined'
                    margin='dense'
                    autoComplete='off'
                    label={`${values.component_footer.length}/${FIELD_COMPONENTS_JSON.FOOTER.MAX_LENGTH}`}
                    InputProps={{ inputProps: { maxLength: FIELD_COMPONENTS_JSON.FOOTER.MAX_LENGTH } } }
                    fullWidth
                  />
                </AccordionDetails>
              </Accordion>
            </DialogContent>

            <DialogActions>
              <Button
                color='inherit'
                variant='outlined'
                disabled={isSubmitting || isFormSubmitting}
                onClick={handleClose}
                className={classes.floatingButton}
              >
                {i18n.t('templateModal.buttons.cancel')}
              </Button>

              <Button
                type='submit'
                color='primary'
                variant='contained'
                disabled={isSubmitting || isFormSubmitting}
                className={`${classes.btnWrapper} ${classes.floatingButton}`}
              >
                {template ? i18n.t('templateModal.buttons.submitUpdate') : i18n.t('templateModal.buttons.submitInsert')}
                {isFormSubmitting && <CircularProgress size={24} className={classes.buttonProgress} />}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default TemplateModal;
